// Cached data validity depends on buildQuery/buildSummaryQuery in comparisonService.ts serializing identical filter params

import type { ComparisonRow, SummaryResponse } from './comparisonService';

const PREFIX = 'pce:cmp:v3:';
const META_KEY = 'pce:cmp:v3:_meta';
const LEGACY_PREFIX = 'pce:cmp:v1:';
const MAX_ENTRIES = 100;
const MAX_EVICT_ROUNDS = 3;

const cacheKey = (url: string): string => `${PREFIX}${url}`;

const getCache = (url: string): ComparisonRow[] | SummaryResponse | null => {
  try {
    const raw = localStorage.getItem(cacheKey(url));
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as { savedAt: number; data: ComparisonRow[] | SummaryResponse };
    return parsed.data ?? null;
  } catch {
    try {
      localStorage.removeItem(cacheKey(url));
    } catch {
      // ignore
    }
    return null;
  }
};

const setCache = (url: string, data: unknown): void => {
  const entry = { savedAt: Date.now(), data };

  // Increment meta count
  try {
    const rawMeta = localStorage.getItem(META_KEY);
    const meta = rawMeta ? (JSON.parse(rawMeta) as { count: number }) : { count: 0 };
    meta.count += 1;
    localStorage.setItem(META_KEY, JSON.stringify(meta));

    if (meta.count > MAX_ENTRIES) {
      evictOldest(meta.count - MAX_ENTRIES);
    }
  } catch {
    // non-fatal: meta bookkeeping failed, proceed with write
  }

  // Attempt write
  try {
    localStorage.setItem(cacheKey(url), JSON.stringify(entry));
  } catch (err) {
    if (!(err instanceof DOMException && err.name === 'QuotaExceededError')) return;

    // Evict oldest entries and retry
    for (let round = 0; round < MAX_EVICT_ROUNDS; round++) {
      try {
        evictOldest(1);
        localStorage.setItem(cacheKey(url), JSON.stringify(entry));
        return; // success
      } catch {
        // retry failed, try next round
      }
    }
    // All rounds failed — cache-write failure is non-fatal; caller still has network data
  }
};

const evictOldest = (count: number): void => {
  try {
    // Collect all PREFIX keys except _meta
    const allKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null && key.startsWith(PREFIX) && key !== META_KEY) {
        allKeys.push(key);
      }
    }

    // Read savedAt for each
    type KeySaved = { key: string; savedAt: number };
    const withSaved: KeySaved[] = [];
    for (const key of allKeys) {
      try {
        const raw = localStorage.getItem(key);
        if (raw === null) continue;
        const parsed = JSON.parse(raw) as { savedAt: number };
        withSaved.push({ key, savedAt: parsed.savedAt });
      } catch {
        // Remove corrupted entries
        try { localStorage.removeItem(key); } catch { /* ignore */ }
      }
    }

    // Sort ascending by savedAt
    withSaved.sort((a, b) => a.savedAt - b.savedAt);

    // Remove oldest `count` entries
    const toRemove = withSaved.slice(0, count);
    for (const { key } of toRemove) {
      try { localStorage.removeItem(key); } catch { /* ignore */ }
    }

    // Decrement meta count
    if (toRemove.length > 0) {
      try {
        const rawMeta = localStorage.getItem(META_KEY);
        const meta = rawMeta ? (JSON.parse(rawMeta) as { count: number }) : { count: 0 };
        meta.count = Math.max(0, meta.count - toRemove.length);
        localStorage.setItem(META_KEY, JSON.stringify(meta));
      } catch {
        // non-fatal
      }
    }
  } catch {
    // completely non-fatal
  }
};

const clearCache = (): void => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null && (key.startsWith(PREFIX) || key.startsWith(LEGACY_PREFIX))) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      try { localStorage.removeItem(key); } catch { /* ignore */ }
    }
  } catch {
    // non-fatal
  }
};

export const comparisonCache = { cacheKey, getCache, setCache, evictOldest, clearCache };
